import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { Ticket, Calendar, MapPin, ShoppingBag, CheckCircle, QrCode } from 'lucide-react';
import { format } from 'date-fns';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cf82e0fe-468c-4a43-97a9-405e8da70dca/artifacts/znleb4a2_260207%20Logo%20Galeries%20Yess5zw.png";

const CartPage = () => {
    const { api } = useAuth();
    const { language } = useLanguage();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    
    const [tickets, setTickets] = useState([]);
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [verifying, setVerifying] = useState(false);

    // Check for success/cancel from URL and verify payment
    useEffect(() => {
        const ticketStatus = searchParams.get('ticket');
        const donationStatus = searchParams.get('donation');
        const sessionId = searchParams.get('session_id');
        
        if (ticketStatus === 'success') {
            if (sessionId) {
                // Verify the payment with backend
                verifyTicketPayment(sessionId);
            } else {
                toast.success('Ticket purchased successfully!');
            }
            // Clean up URL
            window.history.replaceState({}, '', '/cart');
        } else if (ticketStatus === 'cancelled') {
            toast.info('Ticket purchase was cancelled');
            window.history.replaceState({}, '', '/cart');
        } else if (donationStatus === 'success') {
            if (sessionId) {
                verifyDonation(sessionId);
            } else {
                toast.success(language === 'nl' ? 'Bedankt voor je donatie!' : 'Thank you for your donation!');
            }
            window.history.replaceState({}, '', '/cart');
        } else if (donationStatus === 'cancelled') {
            toast.info(language === 'nl' ? 'Donatie geannuleerd' : 'Donation cancelled');
            window.history.replaceState({}, '', '/cart');
        }
    }, [searchParams]);

    const verifyDonation = async (sessionId) => {
        try {
            const response = await api().get(`/tickets/donate/verify/${sessionId}`);
            if (response.data.status === 'paid' || response.data.status === 'complete') {
                toast.success(language === 'nl' ? 'Bedankt voor je donatie!' : 'Thank you for your donation!');
            } else {
                toast.info(language === 'nl' ? 'Donatie wordt verwerkt...' : 'Donation is being processed...');
            }
        } catch (error) {
            console.error('Failed to verify donation:', error);
            toast.success(language === 'nl' ? 'Bedankt voor je donatie!' : 'Thank you for your donation!');
        }
    };

    const verifyTicketPayment = async (sessionId) => {
        setVerifying(true);
        try {
            const response = await api().get(`/tickets/verify/${sessionId}`);
            if (response.data.status === 'paid' || response.data.status === 'complete') {
                toast.success('Ticket purchased successfully!');
            } else {
                toast.info('Payment is being processed...');
            }
            // Refresh tickets
            fetchData();
        } catch (error) {
            console.error('Failed to verify ticket:', error);
            toast.success('Ticket purchased successfully!');
            fetchData();
        } finally {
            setVerifying(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [ticketsRes, eventsRes] = await Promise.all([
                api().get('/tickets/my'),
                api().get('/events/'),
            ]);
            setTickets(ticketsRes.data.filter(t => t.payment_status === 'paid'));
            setEvents(eventsRes.data.filter(e => e.status !== 'archived'));
        } catch (error) {
            console.error('Failed to fetch data:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-white pb-24 overflow-y-auto">
            {/* Fixed Header */}
            <div className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-100 safe-area-top">
                <div className="flex items-center justify-center py-4">
                    <img 
                        src={LOGO_URL} 
                        alt="Galeries Yess" 
                        className="h-8 w-auto"
                    />
                </div>
            </div>

            <div className="pt-20 px-6">
                <h1 className="text-2xl font-heading font-light tracking-tight mb-8">
                    Tickets
                </h1>

                {loading ? (
                    <div className="space-y-4">
                        <Skeleton className="h-32 w-full" />
                        <Skeleton className="h-64 w-full" />
                    </div>
                ) : (
                    <>
                        {/* My Tickets Section */}
                        {tickets.length > 0 ? (
                            <div className="mb-10">
                                <h2 className="text-xs tracking-widest uppercase text-gray-400 mb-4">
                                    {language === 'nl' ? 'Mijn Tickets' : 'My Tickets'}
                                </h2>
                                <div className="space-y-4">
                                    {/* Group tickets by event */}
                                    {events.filter(e => tickets.some(t => t.event_id === e.id)).map((event) => {
                                        const eventTickets = tickets.filter(t => t.event_id === event.id);
                                        const ticketCount = eventTickets.length;
                                        
                                        return (
                                            <div
                                                key={event.id}
                                                className="border border-gray-200 p-6"
                                                data-testid={`ticket-card-${event.id}`}
                                            >
                                                <div className="flex items-start justify-between mb-4">
                                                    <div className="flex-1">
                                                        <h3 className="font-heading text-lg mb-2">
                                                            {event.title || 'Auction Event'}
                                                        </h3>
                                                        {event.start_datetime && (
                                                            <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                                                                <Calendar className="w-4 h-4" />
                                                                <span>{format(new Date(event.start_datetime), 'EEEE, MMMM d, yyyy')}</span>
                                                            </div>
                                                        )}
                                                        {event.location_name && (
                                                            <div className="flex items-center gap-2 text-sm text-gray-500">
                                                                <MapPin className="w-4 h-4" />
                                                                <span>{event.location_name}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center gap-1 text-green-600 text-sm self-start mt-1">
                                                        <Ticket className="w-4 h-4" />
                                                        <span className="font-medium">
                                                            {ticketCount} {ticketCount === 1 ? 'ticket' : 'tickets'}
                                                        </span>
                                                    </div>
                                                </div>
                                                
                                                {/* Thank You Section */}
                                                <div className="pt-4 border-t border-gray-100">
                                                    <p className="text-center italic mb-4" style={{ color: '#F8BBD6' }}>
                                                        {language === 'nl' 
                                                            ? 'Dank je wel! We kijken ernaar uit je te zien op het fashion event!'
                                                            : 'Thank you! We look forward to seeing you at the fashion event!'}
                                                    </p>
                                                    
                                                    <div className="text-sm text-gray-700 space-y-2">
                                                        <p className="font-medium text-black">
                                                            {language === 'nl' ? 'Wat nu?' : "What's next?"}
                                                        </p>
                                                        <p className="text-xs">
                                                            1. {language === 'nl' ? 'Tips om je voor te bereiden →' : 'Tips to get ready →'}{' '}
                                                            <button 
                                                                onClick={(e) => { e.stopPropagation(); navigate('/menu?section=news'); }} 
                                                                className="underline text-[#F8BBD6]"
                                                            >
                                                                News
                                                            </button>
                                                        </p>
                                                        <p className="text-xs">
                                                            2. {language === 'nl' ? 'Doneer kleding voor veiling →' : 'Donate clothes for auction →'}{' '}
                                                            <button 
                                                                onClick={(e) => { e.stopPropagation(); navigate('/my-yess'); }} 
                                                                className="underline text-[#F8BBD6]"
                                                            >
                                                                My Yess
                                                            </button>
                                                        </p>
                                                        <p className="text-xs">
                                                            3. {language === 'nl' ? 'Bekijk alle items →' : 'Preview all items →'}{' '}
                                                            <button 
                                                                onClick={(e) => { e.stopPropagation(); navigate('/menu?section=yess-items'); }} 
                                                                className="underline text-[#F8BBD6]"
                                                            >
                                                                Yess Items
                                                            </button>
                                                        </p>
                                                    </div>
                                                    
                                                    <div className="mt-4 pt-4 border-t border-gray-100 text-center">
                                                        <p className="text-xs text-gray-600 italic">Women. Worth. Second Lives.</p>
                                                        <p className="text-xs text-gray-500">Jessica & Hsiu-Li 🩷</p>
                                                    </div>
                                                    
                                                    {/* QR Code Button */}
                                                    <Button
                                                        onClick={(e) => { e.stopPropagation(); navigate('/profile'); }}
                                                        variant="outline"
                                                        className="w-full mt-4 text-sm"
                                                        data-testid="view-qr-code"
                                                    >
                                                        <QrCode className="w-4 h-4 mr-2" />
                                                        {language === 'nl' ? 'Bekijk je QR-code' : 'View your QR code'}
                                                    </Button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ) : (
                            /* Empty State - No tickets */
                            <div className="text-center py-20">
                                <ShoppingBag className="w-16 h-16 mx-auto text-gray-200 mb-6" />
                                <p className="text-gray-400 mb-2">No tickets yet</p>
                                <p className="text-sm text-gray-300 mb-8">
                                    Get tickets from Auction Events in the Menu
                                </p>
                                <Button
                                    onClick={() => navigate('/menu?section=auction-events')}
                                    className="bg-black hover:bg-black/90 text-white text-sm tracking-widest uppercase font-light"
                                    data-testid="view-events-btn"
                                >
                                    View Auction Events
                                </Button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default CartPage;
